/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { get, set } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Form, Grid, Row, Col } from 'react-bootstrap/lib';
import { getWebsiteDealerContext } from '@upstart/harmony/lib/website-utils';
import DealerService from '../../../../services/DealerService';
import Switch from '../inputs/Switch';
import TextInput from '../inputs/TextInput';
import NumberInput from '../inputs/NumberInput';
import ColorPicker from '../inputs/ColorPicker';
import CodeTextArea from '../inputs/CodeTextArea';
import ImageUpload from '../../../WidgetConfigEditor/inputs/ImageUpload';
import {
  STANDALONE_TRADEIN_BUTTON_CLASSNAME,
  STANDALONE_PREAPPROVAL_BUTTON_CLASSNAME,
  STANDALONE_BROWSE_BUTTON_CLASSNAME,
  widgetsScriptURL,
  ddcWidgetsAdapterURL,
} from '../../../../util/constants';

const STANDALONE_BUTTON_LIST = [
  {
    buttonType: 'tradeInButton',
    title: 'Standalone TradeIn Button',
    placeholder: 'Value Your Trade',
    containerClassName: STANDALONE_TRADEIN_BUTTON_CLASSNAME,
  },
  {
    buttonType: 'preApprovalButton',
    title: 'Standalone Pre-Approved Button',
    placeholder: 'Get Pre-Approved',
    containerClassName: STANDALONE_PREAPPROVAL_BUTTON_CLASSNAME,
  },
  {
    buttonType: 'browseButton',
    title: 'Standalone Browse Button',
    placeholder: 'Click here to Unlock Best Pricing!',
    placeholderUnlocked: 'Browse all vehicles!',
    containerClassName: STANDALONE_BROWSE_BUTTON_CLASSNAME,
  },
];

function StandaloneButtonsEditor(props) {
  const { website, onChange, onSave, formData } = props;

  const onChangeHandler = (e) => {
    set(formData, e.target.name, e.target.value);
    onChange(formData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    onSave();
  };

  const [websiteDealerContext, setWebsiteDealerContext] = useState(
    getWebsiteDealerContext(website),
  );

  useEffect(() => {
    if (!websiteDealerContext) {
      DealerService.getDealer(website?.dealer_id).then((Dealer) => {
        !!Dealer && setWebsiteDealerContext(Dealer);
      });
    }
  }, [website]);

  const renderStandaloneButtonSection = ({
    title,
    buttonType,
    placeholder,
    containerClassName,
    placeholderUnlocked,
  }) => (
    <Col sm={6}>
      <h4>{title}</h4>
      <Switch
        name={`${buttonType}.enabled`}
        formData={formData}
        onChange={onChangeHandler}
      />
      {get(formData, 'useCTAImages') && (
        <ImageUpload
          name={`${buttonType}.image`}
          label="Button Image"
          formData={formData}
          onChange={onChangeHandler}
        />
      )}
      <TextInput
        name={`${buttonType}.text`}
        label="Button Text"
        formData={formData}
        onChange={onChangeHandler}
        placeholder={placeholder}
      />
      {buttonType === 'browseButton' && (
        <TextInput
          name={`${buttonType}.textUnlocked`}
          label="Button Unlocked Text"
          formData={formData}
          onChange={onChangeHandler}
          placeholder={placeholderUnlocked}
        />
      )}
      <NumberInput
        name={`${buttonType}.textSize`}
        label="Button Text Size"
        formData={formData}
        onChange={onChangeHandler}
        info="Enter value in pixels"
      />
      <ColorPicker
        name={`${buttonType}.backgroundColor`}
        label="Button Background Color"
        formData={formData}
        onChange={onChangeHandler}
      />
      <ColorPicker
        name={`${buttonType}.textColor`}
        label="Button Text Color"
        formData={formData}
        onChange={onChangeHandler}
      />
      <CodeTextArea
        label="Button Container"
        value={`<!— Copy this markup into your page template where you want the button to render —>\n<div class="${containerClassName}"></div>`}
      />
    </Col>
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Grid fluid>
        <Row>
          <Col sm={6}>
            <div className="widget-text-toggle-group">
              <Switch
                name="useCTAImages"
                label="CTA Images Instead of Text"
                formData={formData}
                onChange={onChangeHandler}
              />
            </div>
          </Col>
        </Row>
        <Row>{STANDALONE_BUTTON_LIST.map(renderStandaloneButtonSection)}</Row>
        <Row>
          <Col sm={12}>
            <h4>Standard Integration</h4>
            <small>
              Copy the markup below into your page template to render these
              buttons. Only add this script once per page.
            </small>
            <CodeTextArea
              label="Agent Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${widgetsScriptURL}" type="text/javascript" id="prodigyButtonGroupAgent" websiteId="${website.id}"></script>`}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <h4>Dealer Dot Com Integration</h4>
            <small>Provide the following script to DDC support.</small>
            <CodeTextArea
              label="DDC Adapter Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${ddcWidgetsAdapterURL}" type="text/javascript></script>`}
            />
            <small>
              Additionally provide the following websiteId to DDC support.
            </small>
            <CodeTextArea
              label="Website Id"
              value={`websiteId = ${website.id}`}
            />
          </Col>
        </Row>
      </Grid>
    </Form>
  );
}

StandaloneButtonsEditor.propTypes = {
  website: PropTypes.object,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
};

export default StandaloneButtonsEditor;
